import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { TERMS_FALLBACK } from '../legal/termsFallback';
import { formatLegalDate, resolveLegalPolicy } from '../legal/resolveLegalPolicy';
import PageSeo from '../components/seo/PageSeo';

const INTERNAL_LINK_RE = /\[([^\]]+)\]\((\/[^)]+)\)/g;

const renderInline = (text, keyPrefix) => {
  const nodes = [];
  let last = 0;
  let match;
  const re = new RegExp(INTERNAL_LINK_RE.source, 'g');
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(text.slice(last, match.index));
    }
    nodes.push(
      <Link
        key={`${keyPrefix}-${match.index}`}
        to={match[2]}
        className="text-primary dark:text-blue-400 underline hover:no-underline"
      >
        {match[1]}
      </Link>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(text.slice(last));
  }
  return nodes.length ? nodes : text;
};

const TermosDeUso = () => {
  const navigate = useNavigate();
  const [terms, setTerms] = useState(TERMS_FALLBACK);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTerms = async () => {
      try {
        const { data } = await api.get('/lgpd/terms');
        setTerms(resolveLegalPolicy(data, TERMS_FALLBACK, '2.0'));
      } catch (error) {
        console.error('Erro ao carregar termos:', error);
        setTerms(TERMS_FALLBACK);
      } finally {
        setLoading(false);
      }
    };

    fetchTerms();
  }, []);

  const formatContent = (content) => {
    if (typeof content !== 'string') return null;
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('# ')) {
          return null;
        }
        if (line.startsWith('## ')) {
          return (
            <h2 key={index} className="text-2xl font-bold mb-3 mt-6">
              {renderInline(line.substring(3), index)}
            </h2>
          );
        }
        if (line.startsWith('### ')) {
          return (
            <h3 key={index} className="text-xl font-bold mb-2 mt-4">
              {renderInline(line.substring(4), index)}
            </h3>
          );
        }
        if (line.startsWith('- ')) {
          return (
            <li key={index} className="mb-2 ml-4">
              {renderInline(line.substring(2), index)}
            </li>
          );
        }
        if (line.trim() === '') {
          return <br key={index} />;
        }
        return (
          <p key={index} className="mb-4">
            {renderInline(line, index)}
          </p>
        );
      });
  };

  return (
    <div className="min-h-full">
      <PageSeo
        title="Termos de Uso | Ufokko"
        description="Termos de uso da plataforma Ufokko para clientes e videomakers. Regras de serviço, responsabilidades e condições de uso do app."
        path="/termos-de-uso"
      />
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12"
        >
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando termos de uso...</p>
            </div>
          ) : (
            <>
              <div className="mb-8">
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Termos de Uso
                </h1>
                <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <span>Versão {terms.version}</span>
                  <span>•</span>
                  <span>Última atualização: {formatLegalDate(terms.last_updated)}</span>
                </div>
              </div>

              <div className="prose prose-lg dark:prose-invert max-w-none">
                <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
                  {formatContent(terms.content)}
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Ao continuar usando a plataforma, você concorda com estes termos de uso.
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-gradient-to-r from-[#0E76FF] to-[#2563EB] text-white rounded-full font-semibold hover:shadow-lg transition-shadow"
                >
                  Voltar para a página inicial
                </button>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
};

export default TermosDeUso;
